// Dual SRS system with shared sticky label logic
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient'; // make sure this import is correct

export function getWordKey(word, moduleId, groupId) {
  // Use from_text and to_text for normalized structure
  return `flashcard_${moduleId}_${groupId}_${word.from_text}_${word.to_text}`;
}

/** Persist a full SM2 SRS snapshot to `word_srs_data` (e.g. one-step undo). */
export async function persistSm2SrsStateToSupabase(wordId, userId, srs) {
  if (!wordId || !userId) return;
  const lastUpdatedMs = typeof srs.lastUpdated === 'number' ? srs.lastUpdated : Date.now();
  const upsertData = {
    word_id: wordId,
    user_id: userId,
    sm2_ease: srs.sm2_ease ?? 2.5,
    learning_ease: srs.learning_ease ?? 2.5,
    apparent_tier: srs.apparentTier ?? srs.apparent_tier ?? 1,
    history: Array.isArray(srs.history) ? srs.history : [],
    sm2_interval: srs.sm2_interval ?? 0,
    learning_interval: srs.learning_interval ?? 0,
    next_due: srs.nextDue ?? srs.next_due ?? 0,
    updated_at: new Date(lastUpdatedMs).toISOString()
  };
  const { error } = await supabase
    .from('word_srs_data')
    .upsert(upsertData, { onConflict: 'word_id,user_id' });
  if (error) throw error;
}

/** Restore learning-mode fields without writing SM2 interval, ease, or due-date fields. */
export async function persistLearningSrsStateToSupabase(wordId, userId, srs) {
  if (!wordId || !userId) return;
  const lastUpdatedMs = typeof srs.lastUpdated === 'number' ? srs.lastUpdated : Date.now();
  const { error } = await supabase
    .from('word_srs_data')
    .update({
      learning_ease: srs.learning_ease ?? 2.5,
      apparent_tier: srs.apparentTier ?? srs.apparent_tier ?? 1,
      history: Array.isArray(srs.history) ? srs.history : [],
      learning_interval: srs.learning_interval ?? 0,
      updated_at: new Date(lastUpdatedMs).toISOString()
    })
    .eq('word_id', wordId)
    .eq('user_id', userId);
  if (error) throw error;
}

/** Map in-memory SRS snapshot fields onto a `words` row merge (undo / consistency). */
export function srsSnapshotToWordFields(srs) {
  return {
    sm2_interval: srs.sm2_interval ?? 0,
    learning_interval: srs.learning_interval ?? 0,
    sm2_ease: srs.sm2_ease ?? 2.5,
    learning_ease: srs.learning_ease ?? 2.5,
    ease: srs.sm2_ease ?? 2.5,
    next_due: srs.nextDue ?? 0,
    apparent_tier: srs.apparentTier ?? 1,
    history: Array.isArray(srs.history) ? srs.history : []
  };
}

// Helper function to get group ID from groupIdx
export function getGroupId(module, groupIdxOrPosition) {
  // Accept either index or position
  let group = null;
  if (Array.isArray(module.word_groups)) {
    group = module.word_groups.find(g => String(g.position) === String(groupIdxOrPosition) || String(g.id) === String(groupIdxOrPosition));
    if (!group && typeof groupIdxOrPosition === 'number') {
      group = module.word_groups[groupIdxOrPosition];
    }
  }
  return group?.id || `group_${groupIdxOrPosition}`;
}

// Helper function to generate a unique group ID
export function generateGroupId() {
  return `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getDefaultSRS() {
  return {
    sm2_interval: 0,
    learning_interval: 0,
    sm2_ease: 2.5,
    learning_ease: 2.5,
    nextDue: 0,
    history: [],
    apparentTier: 1,
    lastUpdated: Date.now()
  };
}

export function getSRSData(key) {
  const data = localStorage.getItem(key);
  if (!data) return getDefaultSRS();
  try {
    const parsed = JSON.parse(data);
    return {
      ...getDefaultSRS(),
      ...parsed,
      apparentTier: parsed.apparentTier ?? parsed.apparent_tier ?? intervalToTier(parsed.sm2_interval || 0)
    };
  } catch {
    return getDefaultSRS();
  }
}

export function getLearningSRSData(key) {
  const data = localStorage.getItem(key);
  if (!data) {
    return {
      learning_interval: 0,
      history: [],
      lastUpdated: Date.now(),
      apparentTier: 1,
    };
  }
  let parsed;
  try {
    parsed = JSON.parse(data);
  } catch {
    return {
      learning_interval: 0,
      history: [],
      lastUpdated: Date.now(),
      apparentTier: 1,
    };
  }

  if (typeof parsed.learning_interval !== 'number') parsed.learning_interval = 0;
  if (!parsed.history) parsed.history = [];
  if (typeof parsed.apparentTier !== 'number' && typeof parsed.apparent_tier === 'number') {
    parsed.apparentTier = parsed.apparent_tier;
  }
  if (typeof parsed.apparentTier !== 'number') {
    parsed.apparentTier = intervalToTier(parsed.learning_interval || 0);
  }
  if (typeof parsed.lastUpdated !== 'number') parsed.lastUpdated = Date.now();

  return parsed;
}

export function intervalToTier(interval) {
  if (interval >= 10) return 5;
  if (interval >= 4) return 4;
  if (interval >= 2) return 3;
  if (interval >= 1) return 2;
  return 1;
}

function computeNextApparentTier(currentTier, trueTier, knewIt, sm2_interval, learning_interval, hasHistory) {
  let nextTier = currentTier;

  if (currentTier === 1 && !hasHistory) {
    // Word is brand new
    nextTier = knewIt ? 3 : 2;
  } else if (!knewIt) {
    nextTier = Math.max(2, currentTier - 1);
  } else {
    if (trueTier > currentTier) {
      nextTier = currentTier + 1;
      if (nextTier < trueTier) nextTier = trueTier;
      if (nextTier > trueTier + 1) nextTier = trueTier + 1;
    }
  }

  return nextTier;
}

export function updateSRSData(key, knewIt, mode = 'sm2') {
  const now = Date.now();
  let srs = getSRSData(key);
  const history = [...srs.history, { result: knewIt ? 1 : 0, time: now }].slice(-20);
  // Legacy path (localStorage-only) keeps a single ease value for backward compatibility.
  // We treat it as SM2 ease here.
  let sm2Ease = srs.sm2_ease ?? srs.ease ?? 2.5;
  let calculationEase = sm2Ease;

  if (mode === 'sm2') {
    // Calculate ease factor based on performance
    if (knewIt) {
      sm2Ease = Math.min(sm2Ease + 0.1, 3.0); // Standard SM2: increase ease factor, capped at 3.0
    } else {
      sm2Ease = Math.max(1.3, sm2Ease - 0.2); // Standard SM2: decrease ease factor, but never below 1.3
    }
    calculationEase = sm2Ease;
  } else {
    calculationEase = 2.5; // Default ease for learning mode
  }

  // Calculate SM-2 interval independently
  let sm2Interval = srs.sm2_interval;
  if (mode === 'sm2') {
    if (knewIt) {
      sm2Interval = sm2Interval === 0 ? 1 : Math.round(sm2Interval * calculationEase);
      sm2Interval = Math.min(sm2Interval, 365); // Cap interval at 365 days
    } else {
      sm2Interval = 1;
    }
  }

  // Calculate learning interval independently
  let learningInterval = srs.learning_interval;
  if (mode === 'sm2' || mode === 'learning') {
    if (knewIt) {
      learningInterval = learningInterval === 0 ? 1 : Math.round(learningInterval * calculationEase);
      learningInterval = Math.min(learningInterval, 365); // Cap interval at 365 days
    } else {
      learningInterval = 1;
    }
  }

  let nextDue;
  if (mode === 'sm2') {
    nextDue = now + sm2Interval * 86400000;
  } else {
    nextDue = typeof srs.nextDue === 'number' ? srs.nextDue : 0;
  }

  // Always use learning_interval for tier calculation, regardless of mode
  const tierInterval = learningInterval;
  const trueTier = intervalToTier(tierInterval);
  const apparentTier = computeNextApparentTier(
    srs.apparentTier ?? 1,
    trueTier,
    knewIt,
    sm2Interval,
    learningInterval
  );

  const updated = {
    sm2_ease: sm2Ease,
    history,
    nextDue,
    apparentTier,
    lastUpdated: now,
    sm2_interval: sm2Interval,
    learning_interval: learningInterval
  };

  localStorage.setItem(key, JSON.stringify(updated));
  return updated;
}

export function getSRSStatus(srs, t) {
  const tier = srs?.apparentTier ?? 1;
  const map = {
    1: { label: t('newWord'), color: '#aaa' },
    2: { label: t('needsWork'), color: '#e74c3c' },
    3: { label: t('gettingThere'), color: '#f39c12' },
    4: { label: t('nearlyMastered'), color: '#27ae60' },
    5: { label: t('mastered'), color: '#2ecc71' }
  };
  return map[tier] || map[1];
}

export function useSRSState(word, moduleId, groupIdx, userId, preloadedSRSData) {
  // Use fallback group ID for backward compatibility
  const groupId = `group_${groupIdx}`;
  const key = getWordKey(word, moduleId, groupId);
  const [srs, setSRS] = useState(() => {
    const local = getSRSData(key);
    const pre = preloadedSRSData?.[key];
    if (pre && (pre.lastUpdated || 0) > (local.lastUpdated || 0)) {
      localStorage.setItem(key, JSON.stringify(pre));
      return pre;
    }
    return local;
  });

  const updateSRS = useCallback(async (knewIt, onSRSUpdate, mode = 'sm2') => {
    const updated = updateSRSData(key, knewIt, mode);
    setSRS(updated);
    localStorage.setItem(key, JSON.stringify(updated));
    if (onSRSUpdate) onSRSUpdate(key, updated);
    return updated;
  }, [key]);

  useEffect(() => {
    if (preloadedSRSData?.[key]) {
      const pre = preloadedSRSData[key];
      if ((pre.lastUpdated || 0) > (srs.lastUpdated || 0)) {
        setSRS(pre);
      }
    }
  }, [key, preloadedSRSData, srs.lastUpdated]);

  return { srs, updateSRS };
}

// New version that uses stable group IDs
export function useSRSStateWithGroupId(word, module, groupIdx, userId, preloadedSRSData, mode = 'sm2') {
  const groupId = getGroupId(module, groupIdx);
  const key = getWordKey(word, module.id, groupId);
  const [srs, setSRS] = useState(() => {
    // First try to use preloaded data (fresh from Supabase)
    const pre = preloadedSRSData?.[key];
    if (pre) {
      return pre;
    }
    // If no preloaded data, use the word's own SRS data (now from word_srs_data via JOIN)
    if (word) {
      return {
        sm2_interval: word.sm2_interval ?? 0,
        learning_interval: word.learning_interval ?? 0,
        sm2_ease: word.sm2_ease ?? word.ease ?? 2.5,
        learning_ease: word.learning_ease ?? 2.5,
        nextDue: word.next_due ?? 0,
        history: typeof word.history === 'string' ? JSON.parse(word.history) : (word.history || []),
        apparentTier: word.apparent_tier ?? 1,
        lastUpdated: word.srs_updated_at 
          ? new Date(word.srs_updated_at).getTime() 
          : (word.updated_at ? new Date(word.updated_at).getTime() : Date.now())
      };
    }
    // Return default SRS if no data available
    return {
      sm2_interval: 0,
      learning_interval: 0,
      sm2_ease: 2.5,
      learning_ease: 2.5,
      nextDue: 0,
      history: [],
      apparentTier: 1,
      lastUpdated: Date.now()
    };
  });

  const updateSRS = useCallback(async (knewIt, onSRSUpdate, mode = 'sm2') => {
    // Use current SRS state instead of localStorage data
    const currentSRS = srs;
    const now = Date.now();
    const history = [...currentSRS.history, { result: knewIt ? 1 : 0, time: now }].slice(-20);
    // Prefer existing in-memory eases; if missing (e.g. legacy or incomplete preload),
    // fall back to the word's DB-backed eases before defaulting to 2.5
    let sm2Ease = currentSRS.sm2_ease ?? word.sm2_ease ?? word.ease ?? 2.5;
    let learningEase = currentSRS.learning_ease ?? word.learning_ease ?? 2.5;
    let calculationEaseForSm2 = sm2Ease;
    let calculationEaseForLearning = learningEase;

    if (mode === 'sm2') {
      // Update SM2 ease based on performance
      if (knewIt) {
        sm2Ease = Math.min(sm2Ease + 0.1, 3.0);
      } else {
        sm2Ease = Math.max(1.3, sm2Ease - 0.2);
      }
      // Normalize to a few decimal places to avoid floating point artifacts
      sm2Ease = Math.round(sm2Ease * 1000) / 1000;
      if (Math.abs(sm2Ease - 1.3) < 0.001) sm2Ease = 1.3;
      calculationEaseForSm2 = sm2Ease;

      // Also update learning_ease whenever we update learning_interval
      if (knewIt) {
        learningEase = Math.min(learningEase + 0.1, 3.0);
      } else {
        learningEase = Math.max(1.3, learningEase - 0.2);
      }
      // Normalize to a few decimal places to avoid floating point artifacts
      learningEase = Math.round(learningEase * 1000) / 1000;
      if (Math.abs(learningEase - 1.3) < 0.001) learningEase = 1.3;
      calculationEaseForLearning = learningEase;
    } else {
      // Learning-only mode: do not touch SM2 ease; only update learning_ease
      if (knewIt) {
        learningEase = Math.min(learningEase + 0.1, 3.0);
      } else {
        learningEase = Math.max(1.3, learningEase - 0.2);
      }
      // Normalize to a few decimal places to avoid floating point artifacts
      learningEase = Math.round(learningEase * 1000) / 1000;
      if (Math.abs(learningEase - 1.3) < 0.001) learningEase = 1.3;
      calculationEaseForLearning = learningEase;
    }

    // Calculate SM-2 interval independently
    let sm2Interval = currentSRS.sm2_interval ?? 0;
    if (mode === 'sm2') {
      if (knewIt) {
        sm2Interval = sm2Interval === 0 ? 1 : Math.round(sm2Interval * calculationEaseForSm2);
        sm2Interval = Math.min(sm2Interval, 365); // Cap interval at 365 days
      } else {
        sm2Interval = 1;
      }
    }

    // Calculate learning interval independently
    let learningInterval = currentSRS.learning_interval ?? 0;
    if (mode === 'sm2' || mode === 'learning') {
      if (knewIt) {
        learningInterval = learningInterval === 0 ? 1 : Math.round(learningInterval * calculationEaseForLearning);
        learningInterval = Math.min(learningInterval, 365); // Cap interval at 365 days
      } else {
        learningInterval = 1;
      }
    }

    let nextDue;
    if (mode === 'sm2') {
      nextDue = now + sm2Interval * 86400000;
    } else {
      nextDue = typeof currentSRS.nextDue === 'number' ? currentSRS.nextDue : 0;
    }

    // Always use learning_interval for tier calculation, regardless of mode
    const tierInterval = learningInterval;
    const trueTier = intervalToTier(tierInterval);
    const apparentTier = computeNextApparentTier(
      currentSRS.apparentTier ?? 1,
      trueTier,
      knewIt,
      sm2Interval,
      learningInterval
    );

    const updated = {
      sm2_ease: sm2Ease,
      learning_ease: learningEase,
      history,
      nextDue,
      apparentTier,
      lastUpdated: now,
      sm2_interval: sm2Interval,
      learning_interval: learningInterval
    };

    // Update word_srs_data table (optimistic update)
    if (userId && word.id) {
      // Optimistically update local state for instant feedback
      setSRS(updated);

      try {
        const upsertData = {
          word_id: word.id,
          user_id: userId,
          sm2_ease: updated.sm2_ease,
          learning_ease: updated.learning_ease,
          apparent_tier: updated.apparentTier,
          history: updated.history, // JSONB handles this automatically
          updated_at: new Date().toISOString()
        };

        if (mode === 'sm2') {
          // In SM-2 mode, update both intervals
          upsertData.sm2_interval = updated.sm2_interval;
          upsertData.learning_interval = updated.learning_interval;
          upsertData.next_due = updated.nextDue;
        } else if (mode === 'learning') {
          // In learning mode, only update learning interval
          upsertData.learning_interval = updated.learning_interval;
        }

        // Use UPSERT to handle both insert and update
        const { error } = await supabase
          .from('word_srs_data')
          .upsert(upsertData, {
            onConflict: 'word_id,user_id'
          });

        if (error) throw error;

        // If update succeeded, notify the parent callback (if provided)
        if (onSRSUpdate) onSRSUpdate(key, updated);

      } catch (err) {
        console.error('Error updating word SRS data (rolling back):', err);
        // Rollback local state on failure
        setSRS(currentSRS);
        throw err;
      }
    } else {
      // Fallback for non-logged in users (if any still use this path)
      setSRS(updated);
      if (onSRSUpdate) onSRSUpdate(key, updated);
    }

    return updated;
  }, [key, userId, word.id, word.sm2_ease, word.learning_ease, word.ease, srs]);

  useEffect(() => {
    if (preloadedSRSData?.[key]) {
      const pre = preloadedSRSData[key];
      if ((pre.lastUpdated || 0) > (srs.lastUpdated || 0)) {
        setSRS(pre);
      }
    }
  }, [key, preloadedSRSData, srs.lastUpdated]);

  return { srs, updateSRS };
}
